"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Shield, Phone, ArrowRight, CircleCheck as CheckCircle2, Building2, HardHat } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const documentationItems = [{
  title: "Site-specific RAMS",
  description: "Detailed Risk Assessments and Method Statements tailored to your site access layout.",
  icon: FileText
}, {
  title: "Verified accreditations",
  description: "Current NASC and CHAS certificates issued directly for procurement records.",
  icon: Shield
}, {
  title: "Handover & inspection logs",
  description: "Weekly scaffold inspection certificates and safety handover sign-offs ready for auditors.",
  icon: CheckCircle2
}];
export default function Documentation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contact-for-documentation" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border relative overflow-hidden">
      {/* Structural Datum Accent Line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[3px] bg-primary" />

      <div className="container mx-auto px-5 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Heading and Context */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground border border-border text-xs font-semibold uppercase tracking-wider">
              <HardHat className="size-3.5 text-primary" />
              <span>Safety & Procurement Desk</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Need RAMS or site compliance records?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              We issue site-specific risk assessments, method statements, and verified accreditation documentation directly to main contractors, local authorities, and site management teams across the South East.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row flex-wrap gap-3.5">
              <Button asChild size="lg" className="font-semibold shadow-md">
                <Link to="/ContactRequestQuotePage">
                  Request a Quote
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="font-semibold border border-border">
                <a href="tel:01344929900">
                  <Phone className="size-4 mr-1 text-primary" />
                  Call Now
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="font-semibold">
                <Link to="/ContactRequestQuotePage">
                  <Building2 className="size-4 mr-1" />
                  Discuss a Project
                </Link>
              </Button>
            </div>

            <div className="pt-4 border-t border-border flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <span className="inline-block size-2 rounded-full bg-primary" />
                <span>NASC Audited</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block size-2 rounded-full bg-primary" />
                <span>CHAS Accredited</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Documentation Packages List */}
          <div className="lg:col-span-6 grid grid-cols-1 gap-4">
            {documentationItems.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 18
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: shouldReduceMotion ? 0 : i * 0.08,
              ease: [0.16, 1, 0.3, 1]
            }}>
                  <Card className="bg-secondary/40 border border-border/80 rounded-lg transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                    <CardContent className="p-5 sm:p-6 flex items-start gap-4">
                      <div className="p-2.5 rounded-md bg-secondary text-primary border border-border shrink-0">
                        <IconComponent className="size-5" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base sm:text-lg font-semibold text-foreground font-default">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-normal">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>

        </div>
      </div>
    </section>;
}